import { DialogComponent } from "./dialog.js";
import { fetchConfig, debounce, collectCartSections } from "./utilities-v2.js";
import {
  CartAddEvent,
  CartUpdateEvent,
  ThemeEvents,
  SellingPlanUpdateEvent,
} from "./events.js";
import {
  syncGiftQuantitiesSingleFlight,
  getCart,
  primeCartCache,
} from "./pyp-gift-manager.js";

/**
 * @typedef {Object} ParentItemProperties
 * @property {string} [_line_item_id] - Line item ID
 * @property {string} [_parent_product] - Whether this is a parent product
 */

/**
 * @extends {CustomEvent}
 */
export class GiftSelectedEvent extends CustomEvent {
  /**
   * @param {Object} data
   * @param {string} data.variantId
   * @param {string} data.productTitle
   * @param {string} data.parentItemId
   */
  constructor(data) {
    super(GiftSelectedEvent.eventName, { bubbles: true, detail: data });
  }

  static eventName = "gift:selected";
}

/**
 * @extends {CustomEvent}
 */
export class GiftDrawerOpenEvent extends CustomEvent {
  /**
   * @param {Object} data
   * @param {'preview' | 'selection'} data.mode
   * @param {string} [data.parentItemId]
   * @param {string} [data.parentSellingPlanId]
   */
  constructor(data) {
    super(GiftDrawerOpenEvent.eventName, { bubbles: true, detail: data });
  }

  static eventName = "gift-drawer:open";
}

/**
 * @typedef {object} GiftSelectionDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} previewHeader
 * @property {HTMLElement} selectionHeader
 * @property {HTMLElement} selectionTitle
 * @property {HTMLElement} selectionItemsHeading
 * @property {HTMLElement} previewItems
 * @property {HTMLElement} selectionItems
 * @property {HTMLElement} footerText
 * @property {HTMLButtonElement} actionButton
 * @property {HTMLElement} actionButtonText
 * @property {HTMLButtonElement} backButton
 */

/**
 * Gift selection drawer component.
 * Supports preview mode (read-only) and selection mode (interactive).
 * @extends {DialogComponent}
 */
class GiftSelectionDrawerComponent extends DialogComponent {
  /** @type {'preview' | 'selection'} */
  #mode = "preview";

  /** @type {string | null} */
  #selectedGiftId = null;

  /** @type {string | null} */
  #parentItemId = null;

  /** @type {string | null} */
  #parentItemKey = null;

  /** @type {string | null} */
  #parentSellingPlanId = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {boolean} */
  #hasTrackedSelectionView = false;

  /** @type {boolean} */
  #isSwitching = false;

  /** @type {string | null} */
  #existingGiftLineId = null;

  /** @type {string | null} */
  #originalGiftVariantId = null;

  /** @type {number} */
  #parentQuantity = 1;

  /** @type {boolean} */
  #openedFromLearnMore = false;

  /** @type {boolean} */
  #skipAddonsRedirect = false;

  #abortController = new AbortController();

  /**
   * Guards against concurrent async `#processCartForGift` runs.
   * Rapid cart-add bursts can otherwise interleave and clobber shared state.
   * @type {Promise<void> | null}
   */
  #giftProcessingPromise = null;

  /** @type {{ cart: any, variantId: string, isReAdd: boolean } | null} */
  #pendingGiftProcessingArgs = null;

  #debouncedHandleCartAdd = debounce(
    (event) => this.#handleCartAdd(event),
    150,
  );

  /**
   * Primes shared cart cache with latest snapshot.
   * @param {any} cart
   */
  #primeCart(cart) {
    if (cart && Array.isArray(cart.items)) {
      primeCartCache(cart);
    }
  }

  /**
   * Attempts to read a cart object from an event payload.
   * @param {CustomEvent} event
   * @returns {any | null}
   */
  #extractCartFromEvent(event) {
    const resourceCart = event.detail?.resource;
    const resourceWithItems = resourceCart
      ? /** @type {any} */ (resourceCart)
      : null;
    if (resourceWithItems && Array.isArray(resourceWithItems.items)) {
      return resourceWithItems;
    }

    const dataCart = event.detail?.data?.cart;
    const dataWithItems = dataCart ? /** @type {any} */ (dataCart) : null;
    if (dataWithItems && Array.isArray(dataWithItems.items)) {
      return dataWithItems;
    }

    return null;
  }

  /**
   * Cached cart fetch helper.
   * @param {boolean} [force]
   */
  #getCart(force = false) {
    return /** @type {Promise<any>} */ (getCart({ force }));
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(
      CartAddEvent.eventName,
      this.#debouncedHandleCartAdd,
      { signal },
    );
    document.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
      { signal },
    );
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal,
    });
    this.addEventListener("dialog:close", this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== "product-form-component") return;

    const sellingPlanId = event.detail?.data?.sellingPlanId;
    if (!sellingPlanId) return;

    // Skip for product card adds - those go through the added-to-cart drawer first
    const sourceContext = event.detail?.data?.sourceContext;
    if (sourceContext === "product-card") return;

    const variantId = event.detail?.data?.variantId || event.detail?.sourceId;
    if (!variantId) return;

    const addedQuantity = event.detail?.data?.itemCount || 1;
    const cartData = /** @type {any} */ (event.detail?.data)?.cart;

    if (cartData?.items) {
      this.#primeCart(cartData);
      const subscriptionItem = cartData.items.find(
        /** @param {any} item */
        (item) =>
          item.variant_id?.toString() === variantId.toString() &&
          item.selling_plan_allocation &&
          !item.properties?._is_pyp_gift,
      );
      const isReAdd =
        subscriptionItem && subscriptionItem.quantity > addedQuantity;
      this.#enqueueGiftProcessing(cartData, variantId, isReAdd);
    } else {
      this.#fetchCartAndProcess(variantId, true);
    }
  };

  /**
   * Coalesces concurrent cart-add bursts into a single async processing run.
   * If a run is already in-flight, we keep only the latest args and rerun once.
   * @param {any} cart
   * @param {string} variantId
   * @param {boolean} [isReAdd]
   */
  #enqueueGiftProcessing(cart, variantId, isReAdd = false) {
    this.#pendingGiftProcessingArgs = { cart, variantId, isReAdd };
    void this.#drainGiftProcessingQueue();
  }

  async #drainGiftProcessingQueue() {
    if (this.#giftProcessingPromise) return;

    this.#giftProcessingPromise = (async () => {
      while (this.#pendingGiftProcessingArgs) {
        const args = this.#pendingGiftProcessingArgs;
        this.#pendingGiftProcessingArgs = null;
        await this.#processCartForGift(args.cart, args.variantId, args.isReAdd);
      }
    })();

    try {
      await this.#giftProcessingPromise;
    } finally {
      this.#giftProcessingPromise = null;
    }
  }

  /**
   * @param {any} cart
   * @param {string} variantId
   * @param {boolean} [isReAdd]
   */
  async #processCartForGift(cart, variantId, isReAdd = false) {
    this.#primeCart(cart);

    const subscriptionItems = cart.items?.filter(
      /** @param {any} item */
      (item) =>
        item.variant_id?.toString() === variantId.toString() &&
        item.selling_plan_allocation &&
        !item.properties?._is_pyp_gift,
    );

    if (!subscriptionItems || subscriptionItems.length === 0) return;

    const addedItem = subscriptionItems[subscriptionItems.length - 1];

    let existingGiftItem = null;
    for (const subItem of subscriptionItems) {
      const gift = cart.items?.find(
        /** @param {any} item */
        (item) =>
          item.properties?._is_pyp_gift === "true" &&
          item.properties?._parent_item_id === subItem.properties?._line_item_id,
      );
      if (gift) {
        existingGiftItem = gift;
        break;
      }
    }

    const parentUuid = addedItem.properties?._line_item_id;

    const thisItemGift = cart.items?.find(
      /** @param {any} item */
      (item) =>
        item.properties?._is_pyp_gift === "true" &&
        item.properties?._parent_item_id === parentUuid,
    );

    this.#parentItemId = addedItem.id?.toString() || null;
    this.#parentItemKey = addedItem.key || null;
    this.#parentSellingPlanId =
      addedItem.selling_plan_allocation?.selling_plan?.id?.toString() || null;
    this.#parentQuantity = addedItem.quantity || 1;

    // Re-add with existing gift: sync quantity immediately, then open switch drawer
    if (thisItemGift && isReAdd) {
      if (thisItemGift.quantity !== this.#parentQuantity) {
        await this.#updateCartItem(
          thisItemGift.key || thisItemGift.id?.toString(),
          this.#parentQuantity,
        );
        document.dispatchEvent(
          new CartUpdateEvent({}, "gift-selection-drawer", {
            source: "gift-selection-drawer",
          }),
        );
      }

      this.#isSwitching = true;
      this.#existingGiftLineId =
        thisItemGift.key || thisItemGift.id?.toString();
      this.#selectedGiftId = thisItemGift.variant_id?.toString() || null;
      this.#originalGiftVariantId = this.#selectedGiftId;
      this.showSelectionMode();
      return;
    }

    if (thisItemGift) return;

    if (existingGiftItem) {
      if (existingGiftItem.quantity !== this.#parentQuantity) {
        await this.#updateCartItem(
          existingGiftItem.key || existingGiftItem.id?.toString(),
          this.#parentQuantity,
        );
        document.dispatchEvent(
          new CartUpdateEvent({}, "gift-selection-drawer", {
            source: "gift-selection-drawer",
          }),
        );
      }

      this.#isSwitching = true;
      this.#existingGiftLineId =
        existingGiftItem.key || existingGiftItem.id?.toString();
      this.#selectedGiftId = existingGiftItem.variant_id?.toString() || null;
      this.#originalGiftVariantId = this.#selectedGiftId;
    } else {
      this.#isSwitching = false;
      this.#existingGiftLineId = null;
      this.#selectedGiftId = null;
      this.#originalGiftVariantId = null;
    }

    this.showSelectionMode();
  }

  /** @param {string} variantId */
  async #fetchCartAndProcess(variantId, force = false) {
    try {
      const cart = await this.#getCart(force);
      this.#primeCart(cart);
      this.#enqueueGiftProcessing(cart, variantId);
    } catch (error) {
      console.error("Error checking cart for gift eligibility:", error);
    }
  }

  /** @param {SellingPlanUpdateEvent} event */
  #handleSellingPlanUpdate = (event) => {
    if (event.detail?.sellingPlanId) {
      this.#parentSellingPlanId = event.detail.sellingPlanId;
    }
  };

  /** @param {CustomEvent} event */
  #handleCartUpdate = async (event) => {
    const source = event.detail?.data?.source;
    if (
      source === "gift-selection-drawer" ||
      source === "pyp-gift-sync" ||
      source === "product-form-component"
    )
      return;
    if (source !== "cart-items-component") return;

    try {
      const cartFromEvent = this.#extractCartFromEvent(event);
      if (cartFromEvent) {
        this.#primeCart(cartFromEvent);
      }

      const cart = cartFromEvent || (await this.#getCart());
      await syncGiftQuantitiesSingleFlight(cart, { dispatchEvent: true });
    } catch (error) {
      console.error("Error syncing gift quantities:", error);
    }
  };

  showDialog() {
    // Check if opened from learn more drawer
    this.#openedFromLearnMore = this.dataset.openedFromLearnMore === 'true';
    this.showPreviewMode();
  }

  showPreviewMode() {
    this.#mode = "preview";
    this.#selectedGiftId = null;
    this.#updateUIForMode();
    this.#updateBackButtonVisibility();
    super.showDialog();
    this.dispatchEvent(new GiftDrawerOpenEvent({ mode: "preview" }));
  }

  /** @type {string | null} */
  #parentUuid = null;

  /**
  * @param {Object} [options]
  * @param {string} [options.parentItemId]
  * @param {string} [options.parentItemKey]
  * @param {string} [options.parentUuid]
  * @param {string} [options.parentSellingPlanId]
  * @param {string} [options.existingGiftLineId]
  * @param {string} [options.existingGiftVariantId]
  * @param {number} [options.parentQuantity]
  * @param {boolean} [options.skipAddonsRedirect]
  */
  showSelectionMode(options = {}) {
    this.#mode = "selection";

    if (options.parentItemId) this.#parentItemId = options.parentItemId;
    if (options.parentItemKey) this.#parentItemKey = options.parentItemKey;
    if (options.parentUuid) this.#parentUuid = options.parentUuid;  // ← Store UUID
    if (options.parentSellingPlanId)
      this.#parentSellingPlanId = options.parentSellingPlanId;
    if (options.existingGiftLineId) {
      this.#existingGiftLineId = options.existingGiftLineId;
      this.#isSwitching = true;
    }
    if (options.existingGiftVariantId) {
      this.#selectedGiftId = options.existingGiftVariantId;
    } else if (!this.#isSwitching) {
      this.#selectedGiftId = null;
    }
    if (options.parentQuantity) this.#parentQuantity = options.parentQuantity;
    this.#skipAddonsRedirect = options.skipAddonsRedirect ?? false;

    this.#updateUIForMode();
    super.showDialog();

    requestAnimationFrame(() => {
      void this.#trackGiftSelectionViewed();
    });

    this.dispatchEvent(
      new GiftDrawerOpenEvent({
        mode: "selection",
        parentItemId: this.#parentItemId || undefined,
        parentSellingPlanId: this.#parentSellingPlanId || undefined,
      }),
    );
  }

  #updateUIForMode() {
    const {
      previewHeader,
      selectionHeader,
      previewItems,
      selectionItems,
      footerText,
      actionButton,
      actionButtonText,
      selectionTitle,
      selectionItemsHeading,
    } = this.refs;

    const isPreview = this.#mode === "preview";

    if (previewHeader instanceof HTMLElement)
      previewHeader.classList.toggle("hidden", !isPreview);
    if (selectionHeader instanceof HTMLElement)
      selectionHeader.classList.toggle("hidden", isPreview);
    if (previewItems instanceof HTMLElement)
      previewItems.classList.toggle("hidden", !isPreview);
    if (selectionItems instanceof HTMLElement)
      selectionItems.classList.toggle("hidden", isPreview);

    if (!isPreview) {
      if (selectionTitle instanceof HTMLElement) {
        selectionTitle.textContent = this.#isSwitching
          ? this.dataset.switchTitle || "Switch Your Free Gift"
          : this.dataset.selectionTitle || "Select Your Free Gift";
      }
      if (selectionItemsHeading instanceof HTMLElement) {
        selectionItemsHeading.textContent = this.#isSwitching
          ? this.dataset.switchItemsHeading ||
          "Select a different gift for your box"
          : this.dataset.selectionItemsHeading ||
          "Choose one gift to add to every box";
      }
    }

    if (footerText instanceof HTMLElement) {
      if (isPreview) {
        footerText.textContent =
          this.dataset.previewFooterText ||
          "Subscribe to unlock your free gift";
      } else if (this.#isSwitching) {
        footerText.textContent = this.#selectedGiftId
          ? this.dataset.switchFooterText ||
          "Your gift selection will be updated"
          : this.dataset.selectPromptText || "Select a gift to continue";
      } else {
        if (this.#selectedGiftId) {
          footerText.textContent = this.#skipAddonsRedirect
            ? "We'll add your gift to your next order"
            : this.dataset.selectionFooterText || "We'll add your gift to your next order";
        } else {
          footerText.textContent = this.dataset.selectPromptText || "Select a gift to continue";
        }
      }
    }

    if (
      actionButton instanceof HTMLButtonElement &&
      actionButtonText instanceof HTMLElement
    ) {
      if (isPreview) {
        actionButtonText.textContent =
          this.dataset.previewButtonText || "Close Preview";
        actionButton.disabled = false;
        actionButton.classList.remove(
          "bg-grayscale-coal-100",
          "text-grayscale-coal-600",
        );
        actionButton.classList.add("bg-yolk-500", "text-night-900");
      } else {
        if (this.#isSwitching) {
          actionButtonText.textContent = this.dataset.switchButtonText || "Switch Gift";
        } else if (this.#skipAddonsRedirect) {
          actionButtonText.textContent = "Claim Gift";
        } else {
          actionButtonText.textContent = this.dataset.claimButtonText || "Continue to add-ons";
        }

        actionButton.disabled = !this.#selectedGiftId || this.#loading;

        if (!this.#selectedGiftId) {
          actionButton.classList.add(
            "bg-grayscale-coal-100",
            "text-grayscale-coal-600",
          );
          actionButton.classList.remove("bg-yolk-500", "text-night-900");
        } else {
          actionButton.classList.remove(
            "bg-grayscale-coal-100",
            "text-grayscale-coal-600",
          );
          actionButton.classList.add("bg-yolk-500", "text-night-900");
        }
      }
    }

    this.#updateBackButtonVisibility();
    this.#updateGiftItemsUI();
  }

  #updateBackButtonVisibility() {
    const { backButton } = this.refs;
    if (backButton instanceof HTMLButtonElement) {
      backButton.classList.toggle("hidden", !this.#openedFromLearnMore);
    }
  }

  handleBackButton() {
    this.#openedFromLearnMore = false;
    delete this.dataset.openedFromLearnMore;
    this.closeDialog();
  }

  handleCloseButton() {
    if (this.#openedFromLearnMore) {
      document.dispatchEvent(new CustomEvent('gift-drawer:close-requested', {
        detail: { fromLearnMore: true }
      }));
    }
    this.closeDialog();
  }

  #updateGiftItemsUI() {
    const giftItems = this.querySelectorAll("[data-gift-item]");

    giftItems.forEach((item) => {
      const variantId = item.getAttribute("data-variant-id");
      const isSelected = variantId === this.#selectedGiftId;
      const isPreview = this.#mode === "preview";

      item.setAttribute("data-selected", String(isSelected));
      item.setAttribute("aria-pressed", String(isSelected));

      if (isPreview) {
        item.classList.remove(
          "p-[8px]",
          "cursor-pointer",
          "bg-white",
          "rounded-[4px]",
          "border-[3px]",
          "border-brown-600",
          "border-[1px]",
          "bg-gradient-to-r",
          "from-[rgba(189,176,147,0.3)]",
          "to-[rgba(189,176,147,0.3)]",
        );
        item.classList.add(
          "px-0",
          "py-[8px]",
          "h-[101px]",
          "border-t",
          "border-grayscale-coal-200",
        );
      } else {
        item.classList.remove("px-0", "py-[8px]", "h-[101px]", "border-t");
        item.classList.add(
          "p-[8px]",
          "h-[106px]",
          "cursor-pointer",
          "bg-white",
          "rounded-[4px]",
        );

        if (isSelected) {
          item.classList.remove("border-[1px]", "border-grayscale-coal-200");
          item.classList.add(
            "border-[3px]",
            "border-brown-600",
            "bg-gradient-to-r",
            "from-[rgba(189,176,147,0.3)]",
            "to-[rgba(189,176,147,0.3)]",
          );
        } else {
          item.classList.remove(
            "border-[3px]",
            "border-brown-600",
            "bg-gradient-to-r",
            "from-[rgba(189,176,147,0.3)]",
            "to-[rgba(189,176,147,0.3)]",
          );
          item.classList.add("border-[1px]", "border-grayscale-coal-200");
        }
      }
    });
  }

  /**
   * @param {MouseEvent} event
   * @param {Object} _data
   */
  selectGift(event, _data) {
    if (this.#mode === "preview") return;

    const target =
      event.target instanceof Element
        ? event.target.closest("[data-gift-item]")
        : null;
    if (!target) return;

    const variantId = target.getAttribute("data-variant-id");
    const productTitle = target.getAttribute("data-product-title");

    if (variantId) {
      this.#selectedGiftId = variantId;
      this.#updateUIForMode();

      void this.#trackGiftSelected(variantId, productTitle || "");

      this.dispatchEvent(
        new GiftSelectedEvent({
          variantId,
          productTitle: productTitle || "",
          parentItemId: this.#parentItemId || "",
        }),
      );
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {Object} _data
   */
  handleGiftKeydown(event, _data) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectGift(event, _data);
    }
  }

  async handleAction() {
    if (this.#mode === "preview") {
      this.closeDialog();
      return;
    }

    if (!this.#selectedGiftId || !this.#parentItemKey) {
      console.error("Cannot add gift: missing gift ID or parent item key");
      return;
    }

    if (
      this.#isSwitching &&
      this.#selectedGiftId === this.#originalGiftVariantId
    ) {
      this.closeDialog();
      return;
    }

    await this.#addGiftToCart();
  }

  #resetState() {
    this.#selectedGiftId = null;
    this.#originalGiftVariantId = null;
    this.#parentItemId = null;
    this.#parentItemKey = null;
    this.#parentUuid = null;  // ← ADD THIS
    this.#parentSellingPlanId = null;
    this.#existingGiftLineId = null;
    this.#isSwitching = false;
    this.#skipAddonsRedirect = false;
    this.#parentQuantity = 1;
    this.#hasTrackedSelectionView = false;
  }

  #handleDialogClose = () => {
    delete this.dataset.openedFromLearnMore;
    this.#openedFromLearnMore = false;

    this.#resetState();
  };

  async #getAnalyticsCartContext() {
    if (!window.grAnalytics?.buildCartContextFromCart) return undefined;

    try {
      const cart = await this.#getCart();
      return window.grAnalytics.buildCartContextFromCart(cart);
    } catch (error) {
      console.error("[GiftSelection] Failed to get cart for analytics:", error);
      return undefined;
    }
  }

  async #trackGiftSelectionViewed() {
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement && !dialog.open) return;
    
    if (this.#hasTrackedSelectionView) return;
    this.#hasTrackedSelectionView = true;

    const cartContext = await this.#getAnalyticsCartContext();
    window.grAnalytics?.trackGiftSelectionViewed({}, cartContext);
  }

  async #trackGiftSelected(variantId, productTitle) {
    const cartContext = await this.#getAnalyticsCartContext();
    window.grAnalytics?.trackGiftSelected(
      {
        gift_product_name: productTitle || undefined,
        gift_variant_id: variantId || undefined,
      },
      cartContext,
    );
  }

  async #addGiftToCart() {
    if (this.#loading) return;
    this.#loading = true;
    this.#updateUIForMode();

    try {
      if (this.#existingGiftLineId) {
        try {
          await this.#updateCartItem(this.#existingGiftLineId, 0);
        } catch (error) {
          console.error(
            "Error removing existing gift before switching:",
            error,
          );
          return;
        }
      }

      if (!this.#selectedGiftId) {
        console.error("No gift selected");
        return;
      }

      // Use stored UUID or fetch/generate it
      let parentUuid = this.#parentUuid;

      if (!parentUuid) {
        // Fallback: fetch cart and find parent by key
        if (!this.#parentItemKey) {
          console.error("Cannot add gift: parent UUID and key are both missing");
          return;
        }

        const cart = await this.#getCart(true);

        // Find parent by key (reliable unique identifier)
        const parentItem = cart.items?.find(
          /** @param {any} item */
          (item) => item.key === this.#parentItemKey
        );

        if (!parentItem) {
          console.error("Parent item not found in cart", {
            parentItemKey: this.#parentItemKey,
          });
          return;
        }

        const props = /** @type {ParentItemProperties} */ (parentItem.properties || {});
        parentUuid = props._line_item_id || null;

        if (!parentUuid) {
          // Generate new UUID for parent
          parentUuid = crypto.randomUUID();
          await this.#addParentProperties(parentItem.key, parentUuid);
        }
      }

      const properties = {
        _is_pyp_gift: "true",
        _parent_item_id: parentUuid,
      };

      const formData = new FormData();
      formData.append("id", this.#selectedGiftId);
      formData.append("quantity", String(this.#parentQuantity));

      for (const [key, value] of Object.entries(properties)) {
        formData.append(`properties[${key}]`, value);
      }

      const sectionsToUpdate = collectCartSections();
      if (sectionsToUpdate.length > 0) {
        formData.append("sections", sectionsToUpdate.join(","));
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig("javascript", { body: formData }),
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (data.status) {
        console.error("Error adding gift to cart:", data.message);
        return;
      }

      if (data.cart) {
        this.#primeCart(data.cart);
      }

      this.dispatchEvent(
        new CartUpdateEvent({}, "gift-selection-drawer", {
          source: "gift-selection-drawer",
          sections: data.sections,
          cart: data.cart,
          itemCount: data.item_count,
        }),
      );

      this.closeDialog();

      if (!this.#isSwitching && !this.#skipAddonsRedirect && !window.location.pathname.includes("/pre-checkout-add-ons")) {
        const addonsUrl = this.dataset.addonsUrl || "/collections/pre-checkout-add-ons";
        window.location.href = addonsUrl;
      }
    } catch (error) {
      console.error("Error adding gift to cart:", error);
    } finally {
      this.#loading = false;
      this.#updateUIForMode();
    }
  }

  /**
   * @param {string} lineId
   * @param {number} quantity
   */
  async #updateCartItem(lineId, quantity) {
    const body = JSON.stringify({ id: lineId, quantity });
    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body }),
    );
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Cart change failed (${response.status}): ${text}`);
    }

    // Some Shopify endpoints return JSON even for change.js; parse defensively to detect errors.
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.json().catch(() => null);
      if (data?.errors) {
        throw new Error(`Cart change error: ${JSON.stringify(data.errors)}`);
      }
    }
  }

  /** @returns {'preview' | 'selection'} */
  get mode() {
    return this.#mode;
  }

  /** @returns {string | null} */
  get selectedGiftId() {
    return this.#selectedGiftId;
  }

  /**
  * Adds _line_item_id and _parent_product properties to a parent item
  * @param {string} parentLineItemKey - The parent's line item key
  * @param {string} uuid - The UUID to use as the line item ID
  */
  async #addParentProperties(parentLineItemKey, uuid) {
    try {
      // Fetch current cart to get the parent item
      const cart = await this.#getCart(true);
      const parentItem = cart.items?.find(
        /** @param {any} item */
        (item) => item.key === parentLineItemKey
      );

      if (!parentItem) {
        console.error("Parent item not found in cart");
        return;
      }

      // Check if properties already exist - skip if they do
      const props = /** @type {ParentItemProperties} */ (parentItem.properties || {});
      if (props._line_item_id && props._parent_product === "yes") {
        console.log("[Parent Properties] Already exist, skipping update");
        return;
      }

      const body = JSON.stringify({
        id: parentItem.key,
        quantity: parentItem.quantity,
        properties: {
          ...(parentItem.properties || {}),
          _line_item_id: uuid,
          _parent_product: "yes",
        },
      });

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", { body }),
      );

      if (!response.ok) {
        console.error("Failed to add parent properties");
      }
    } catch (error) {
      console.error("Error adding parent properties:", error);
    }
  }

}

if (!customElements.get("gift-selection-drawer")) {
  customElements.define("gift-selection-drawer", GiftSelectionDrawerComponent);
}
